import {
  autoUpdate,
  computePosition,
  flip,
  offset,
  type Placement,
  shift,
  size,
} from "@floating-ui/dom";
import { AttributeParser } from "@/utils/attribute-parser/attribute-parser";

export class DropdownV2 extends HTMLElement {
  protected attr: AttributeParser;

  public readonly placement: Placement;
  public readonly offset: number;

  private readonly toggle: HTMLElement;
  private readonly menu: HTMLElement;

  private controller?: AbortController;
  private openController?: AbortController;

  private autoUpdateCleanup?: () => void;

  constructor() {
    super();

    this.attr = new AttributeParser(this);

    this.placement = this.attr.literal(
      "placement",
      [
        "top",
        "top-start",
        "top-end",
        "left",
        "left-start",
        "left-end",
        "right",
        "right-start",
        "right-end",
        "bottom",
        "bottom-start",
        "bottom-end",
      ],
      "bottom",
    );

    this.offset = this.attr.number("offset", 8);

    const toggle = Array.from(this.children).find(
      (children) => children.tagName.toLowerCase() !== "dropdown-v2-menu",
    );

    if (toggle instanceof HTMLElement === false) {
      throw new Error(
        "DropdownV2Error: No toggle found in dropdown-v2 Element",
      );
    }

    this.toggle = toggle;

    const menu = Array.from(this.children).find(
      (children) => children.tagName.toLowerCase() === "dropdown-v2-menu",
    );

    if (menu instanceof HTMLElement === false) {
      throw new Error(
        "DropdownV2Error: No dropdown-v2-menu found in dropdown-v2 Element",
      );
    }

    this.menu = menu;
  }

  connectedCallback() {
    this.controller = new AbortController();

    this.toggle.addEventListener(
      "click",
      () => {
        if (this.toggle.classList.contains("active") === false) {
          this.setOpen(true);
        } else {
          this.setOpen(false);
        }
      },
      {
        signal: this.controller.signal,
      },
    );
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.openController?.abort();
  }

  public setOpen = (open: boolean) => {
    this.openController?.abort();
    this.autoUpdateCleanup?.();

    this.toggle.classList.toggle("active", open);

    if (open === true) {
      this.openController = new AbortController();

      document.body.appendChild(this.menu);

      this.autoUpdateCleanup = autoUpdate(this.toggle, this.menu, this.update);

      document.addEventListener(
        "pointerdown",
        (event) => {
          if (event.target instanceof Node) {
            if (
              this.toggle.contains(event.target) === false &&
              this.menu.contains(event.target) === false
            ) {
              this.setOpen(false);
            }
          }
        },
        {
          signal: this.openController.signal,
        },
      );

      document.addEventListener(
        "keydown",
        (event) => {
          if (event.code === "Escape") {
            this.setOpen(false);
          }
        },
        {
          signal: this.openController.signal,
        },
      );

      this.openController.signal.addEventListener("abort", () => {
        this.toggle.classList.remove("active");
        this.menu.removeAttribute("style");
        this.appendChild(this.menu);
      });
    }
  };

  private update = async () => {
    const { x, y } = await computePosition(this.toggle, this.menu, {
      strategy: "fixed",
      placement: this.placement,
      middleware: [
        offset(this.offset),
        flip(),
        shift({
          mainAxis: true,
          crossAxis: true,
          padding: this.offset,
        }),
        size({
          apply: ({ availableHeight, elements }) => {
            elements.floating.style.setProperty(
              "max-height",
              `${availableHeight - this.offset}px`,
            );
          },
        }),
      ],
    });

    Object.assign(this.menu.style, {
      left: `${x}px`,
      top: `${y}px`,
      minWidth: `${this.toggle.clientWidth}px`,
    });
  };
}
